import type { SelectChangeEvent } from '@mui/material';
import { InputLabel } from '@mui/material';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from 'features/app/hooks';
import { TeamingReason } from 'features/teaming/teaming-base/enums';
import { setTeamingReason } from 'features/teaming/teaming-base/store';
import type { Gender, Language } from 'config/enums';
import { HandoffRequestContainer } from 'features/collaboration-base/components';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import { SelectGender, SelectLanguage } from 'features/ui/components';
import { Button } from 'features/ui/elements';

interface HandoffRequestProps {
  gender: Gender;
  language: Language;
  onGenderChange: (value: Gender) => void;
  onLanguageChange: (value: Language) => void;
  isLoading: boolean;
  onRequestHandoff: () => void;
  onClose: () => void;
}

const Label = styled(InputLabel)`
  margin-bottom: ${theme.spacing(3)};
`;

const LanguageLabel = styled(Label)`
  margin-top: ${theme.spacing(8)};
`;

const SubmitButton = styled(Button)`
  margin-top: ${theme.spacing(8)};
`;

const CancelButton = styled(Button)`
  margin-top: ${theme.spacing(4)};
`;

export const HandoffRequest: FC<HandoffRequestProps> = ({
  gender,
  onGenderChange,
  isLoading,
  onRequestHandoff,
  onClose,
  language,
  onLanguageChange,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const handleGenderChange = (event: SelectChangeEvent<Gender>) => {
    onGenderChange(event.target.value as Gender);
  };

  const handleLanguageChange = (event: SelectChangeEvent<Language>) => {
    onLanguageChange(event.target.value as Language);
  };

  const handleRequestClick = () => {
    dispatch(setTeamingReason(TeamingReason.Handoff));
    onRequestHandoff();
  };

  return (
    <HandoffRequestContainer testId='handoff-request'>
      <Label>{t('genderPreferenceQuestion')}</Label>
      <SelectGender value={gender} onChange={handleGenderChange} />
      <LanguageLabel>{t('teamRequest.requestLanguage')}</LanguageLabel>
      <SelectLanguage value={language} onChange={handleLanguageChange} />
      <SubmitButton
        type='submit'
        color='primary'
        size='small'
        fullWidth
        title={t('requestHandoff')}
        onClick={handleRequestClick}
        loading={isLoading}
      >
        {t('requestHandoff')}
      </SubmitButton>
      <CancelButton
        color='secondary'
        size='small'
        fullWidth
        title={t('cancel')}
        onClick={onClose}
      >
        {t('cancel')}
      </CancelButton>
    </HandoffRequestContainer>
  );
};
